/**
 * Advanced Background Animation System
 * GPU-accelerated, interactive animations for StudioDome
 */

class AdvancedBackgrounds {
  constructor() {
    this.animations = new Map();
    this.activeAnimation = null;
    this.container = null;
  }

  init(container) {
    this.container = container || document.body;
    this.setupAnimations();

    // Auto-initialize animations on elements with bg-* classes
    this.autoInitialize();
  }

  autoInitialize() {
    // EMERGENCY BRAKE: Check for disabled animations
    if (window.location.search.includes('animations=false') ||
            localStorage.getItem('disable-animations') === 'true') {
      return;
    }

    // PERFORMANCE: Check if performance mode or reduced effects is enabled
    const performanceMode =
      document.documentElement.hasAttribute('data-performance-mode') ||
      document.documentElement.hasAttribute('data-reduced-effects') ||
      document.body?.hasAttribute('data-performance-mode') ||
      document.body?.hasAttribute('data-reduced-effects') ||
      window.performanceMode?.performanceMode ||
      window.performanceMode?.reducedEffects ||
      window.location.search.includes('performance=true') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (performanceMode) {
      console.log('[AdvancedBackgrounds] Skipped - performance/reduced mode active');
      return;
    }

    // Find all elements with advanced background classes
    const backgroundElements = document.querySelectorAll([
      '.bg-neural-network',
      '.bg-particle-universe',
      '.bg-liquid-metal',
      '.bg-blood-cells',
      '.bg-cyber-rain',
      '.bg-quantum-field',
      '.bg-horror-tentacles',
      '.bg-plasma-storm',
      '.bg-dna-helix',
      '.bg-fractal-zoom',
      '.bg-blood-drip',
      '.bg-blood-splatter',
      '.bg-creeping-shadows',
      '.bg-horror-mist'
    ].join(', '));

    // DEBUG: Allow more animations for debugging
    const maxConcurrentAnimations = Math.min(8, Math.floor(navigator.hardwareConcurrency) || 6);
    let activeAnimations = 0;

    // PERFORMANCE: Use Intersection Observer to only animate visible elements
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && activeAnimations < maxConcurrentAnimations) {
            // Add delay to prevent all animations starting simultaneously
            // Initialize immediately for debugging
            this.initializeAnimation(entry.target);
            activeAnimations++;
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '100px', // Larger margin for earlier detection
        threshold: 0.25 // Higher threshold for more visible area required
      });

      backgroundElements.forEach(element => {
        observer.observe(element);
      });
    } else {
      // Fallback: Initialize only ONE animation to prevent system overload
      if (backgroundElements.length > 0) {
        this.initializeAnimation(backgroundElements[0]);
      }
    }
  }

  initializeAnimation(element) {
    try {
      // Get the animation type from the class name
      const classList = Array.from(element.classList);
      const bgClass = classList.find(cls => cls.startsWith('bg-') && cls !== 'bg-advanced' && cls !== 'bg-animated' && cls !== 'bg-advanced-fullscreen');

      if (bgClass) {
        const animationType = bgClass.replace('bg-', '').replace(/-/g, '-');

        const AnimationClass = this.getAnimationClass(animationType);

        if (AnimationClass) {
          // Create a new instance for this element
          const animationInstance = new AnimationClass();
          animationInstance.init(element);
          animationInstance.start();
        }
      }
    } catch (error) {
      // Continue with other animations even if one fails
    }
  }

  getAnimationClass(type) {
    switch (type) {
      case 'neural-network': return NeuralNetworkAnimation;
      case 'particle-universe': return ParticleUniverseAnimation;
      case 'liquid-metal': return LiquidMetalAnimation;
      case 'blood-cells': return BloodCellsAnimation;
      case 'cyber-rain': return CyberRainAnimation;
      case 'quantum-field': return QuantumFieldAnimation;
      case 'horror-tentacles': return HorrorTentaclesAnimation;
      case 'plasma-storm': return PlasmaStormAnimation;
      case 'dna-helix': return DNAHelixAnimation;
      case 'fractal-zoom': return FractalZoomAnimation;
      case 'blood-drip': return BloodDripAnimation;
      case 'blood-splatter': return BloodSplatterAnimation;
      case 'creeping-shadows': return CreepingShadowsAnimation;
      case 'horror-mist': return HorrorMistAnimation;
      default: return null;
    }
  }

  setupAnimations() {
    // Register all available animations
    this.registerAnimation('neural-network', new NeuralNetworkAnimation());
    this.registerAnimation('particle-universe', new ParticleUniverseAnimation());
    this.registerAnimation('liquid-metal', new LiquidMetalAnimation());
    this.registerAnimation('blood-cells', new BloodCellsAnimation());
    this.registerAnimation('cyber-rain', new CyberRainAnimation());
    this.registerAnimation('quantum-field', new QuantumFieldAnimation());
    this.registerAnimation('horror-tentacles', new HorrorTentaclesAnimation());
    this.registerAnimation('plasma-storm', new PlasmaStormAnimation());
    this.registerAnimation('dna-helix', new DNAHelixAnimation());
    this.registerAnimation('fractal-zoom', new FractalZoomAnimation());
    this.registerAnimation('blood-drip', new BloodDripAnimation());
    this.registerAnimation('blood-splatter', new BloodSplatterAnimation());
    this.registerAnimation('creeping-shadows', new CreepingShadowsAnimation());
    this.registerAnimation('horror-mist', new HorrorMistAnimation());
  }

  registerAnimation(name, animation) {
    this.animations.set(name, animation);
  }

  activate(name) {
    if (this.activeAnimation) {
      this.activeAnimation.destroy();
    }

    const animation = this.animations.get(name);
    if (animation) {
      this.activeAnimation = animation;
      animation.init(this.container);
      animation.start();
    }
  }

  destroy() {
    if (this.activeAnimation) {
      this.activeAnimation.destroy();
      this.activeAnimation = null;
    }
  }
}

/**
 * Base class for all advanced animations
 */
class BaseAnimation {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.animationId = null;
    this.width = 0;
    this.height = 0;
    this.time = 0;
    this.mouseX = 0;
    this.mouseY = 0;
  }

  init(container) {
    try {
      if (!container) {
        throw new Error('Container element is required');
      }

      this.container = container;

      this.createCanvas(container);
      this.setupEventListeners();
      this.onResize();
    } catch (error) {
      throw error;
    }
  }

  createCanvas(container) {
    try {
      // Get container dimensions first
      const rect = container.getBoundingClientRect();
      this.width = rect.width;
      this.height = rect.height;

      this.canvas = document.createElement('canvas');
      // Set canvas dimensions immediately to prevent clearing during resize
      this.canvas.width = this.width;
      this.canvas.height = this.height;
      this.canvas.style.position = 'absolute';
      this.canvas.style.top = '0';
      this.canvas.style.left = '0';
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';
      this.canvas.style.zIndex = '-1';
      this.canvas.style.pointerEvents = 'none';

      this.ctx = this.canvas.getContext('2d', {
        alpha: true,
        desynchronized: true
      });

      if (!this.ctx) {
        throw new Error('Failed to get 2D rendering context');
      }

      container.appendChild(this.canvas);
      container.classList.add('has-advanced-bg');
    } catch (error) {
      throw error;
    }
  }

  setupEventListeners() {
    this.resizeHandler = () => this.onResize();
    this.mouseMoveHandler = (e) => this.onMouseMove(e);

    // Use ResizeObserver for robust size tracking (handles visibility changes)
    if ('ResizeObserver' in window && this.container) {
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this.container);
    } else {
      window.addEventListener('resize', this.resizeHandler);
    }
    
    window.addEventListener('mousemove', this.mouseMoveHandler);
  }

  onResize() {
    // Get container dimensions instead of window dimensions
    if (this.container) {
      const rect = this.container.getBoundingClientRect();
      const newWidth = rect.width;
      const newHeight = rect.height;

      // Only resize canvas if dimensions actually changed
      if (newWidth !== this.width || newHeight !== this.height) {
        this.width = newWidth;
        this.height = newHeight;
        if (this.canvas) {
          this.canvas.width = this.width;
          this.canvas.height = this.height;
        }
        
        // Force a re-initialization of particles/sources if size changed significantly (e.g. from 0 to visible)
        if (this.width > 0 && this.height > 0 && (this.width !== 0 || this.height !== 0)) {
           // Optional: Trigger a re-generation or adjust logic if needed by subclasses
           // For now, most subclasses handle random generation based on current width/height in render loop or init
           // But for fixed-count items generated in init(), we might need to regenerate.
           // However, let's trust that init() generated something even if 0x0 (using fallback) or we just accept it.
           // Actually, init() uses this.width/this.height. If 0, it might generate 0 items.
           
           // Check if we need to regenerate content
           if (typeof this.onResizeContent === 'function') {
             this.onResizeContent();
           }
        }
      }
    } else {
      // Fallback to window dimensions
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;

      if (newWidth !== this.width || newHeight !== this.height) {
        this.width = newWidth;
        this.height = newHeight;
        if (this.canvas) {
          this.canvas.width = this.width;
          this.canvas.height = this.height;
        }
      }
    }
  }

  onMouseMove(e) {
    // Calculate mouse position relative to the container
    if (this.container) {
      const rect = this.container.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
    } else {
      // Fallback to window coordinates
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
    }
  }

  start() {
    // PERFORMANCE: Initialize frame rate limiting
    this.targetFPS = 30; // Lower FPS to reduce CPU/GPU load
    this.frameInterval = 1000 / this.targetFPS;
    this.lastFrameTime = 0;
    this.frameDelta = 0;

    this.animate();
  }

  animate() {
    // PERFORMANCE: Frame rate limiting to prevent CPU/GPU murder
    const currentTime = performance.now();
    this.frameDelta = currentTime - this.lastFrameTime;

    if (this.frameDelta >= this.frameInterval) {
      this.time += 0.01;
      this.render();
      this.lastFrameTime = currentTime - (this.frameDelta % this.frameInterval);
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  render() {
    // Override in subclasses
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    window.removeEventListener('mousemove', this.mouseMoveHandler);

    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

/**
 * Neural Network Animation
 * Interactive nodes connected by synapses that pulse with activity
 */
class NeuralNetworkAnimation extends BaseAnimation {
  constructor() {
    super();
    this.nodes = [];
    this.connections = [];
    this.pulses = [];
  }

  init(container) {
    super.init(container);
    this.generateNetwork();
  }

  generateNetwork() {
    // Create nodes
    const nodeCount = Math.min(100, Math.floor(this.width * this.height / 10000));

    for (let i = 0; i < nodeCount; i++) {
      this.nodes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 3 + 2,
        activity: Math.random(),
        neighbors: []
      });
    }

    // Create connections
    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const dx = this.nodes[i].x - this.nodes[j].x;
        const dy = this.nodes[i].y - this.nodes[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          this.connections.push({
            start: this.nodes[i],
            end: this.nodes[j],
            strength: 1 - distance / 150
          });
          this.nodes[i].neighbors.push(this.nodes[j]);
          this.nodes[j].neighbors.push(this.nodes[i]);
        }
      }
    }
  }

  render() {
    // SIMPLE BRIGHT RED TEST - should be very visible
    this.ctx.fillStyle = 'red';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Add a white rectangle to make it obvious
    this.ctx.fillStyle = 'white';
    this.ctx.fillRect(10, 10, this.width - 20, this.height - 20);

    // Add debug text
    this.ctx.fillStyle = 'black';
    this.ctx.font = '20px Arial';
    this.ctx.fillText('CANVAS WORKING!', 20, 50);
    this.ctx.fillText(`${this.width}x${this.height}`, 20, 80);

    return; // Skip the complex animation for now

    // Update and draw connections
    this.ctx.strokeStyle = 'rgba(129, 140, 248, 0.3)';
    this.ctx.lineWidth = 1;

    for (const connection of this.connections) {
      const dx = connection.end.x - connection.start.x;
      const dy = connection.end.y - connection.start.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 200) {
        const opacity = connection.strength * (1 - distance / 200);

        // Pulse effect near mouse
        const mouseDist = Math.min(
          this.getDistanceToMouse(connection.start.x, connection.start.y),
          this.getDistanceToMouse(connection.end.x, connection.end.y)
        );

        const pulseFactor = mouseDist < 200 ? 1 + (1 - mouseDist / 200) : 1;

        this.ctx.strokeStyle = `rgba(129, 140, 248, ${opacity * pulseFactor * 0.5})`;
        this.ctx.beginPath();
        this.ctx.moveTo(connection.start.x, connection.start.y);
        this.ctx.lineTo(connection.end.x, connection.end.y);
        this.ctx.stroke();
      }
    }

    // Update and draw nodes
    for (const node of this.nodes) {
      // Update position
      node.x += node.vx;
      node.y += node.vy;

      // Bounce off walls
      if (node.x < 0 || node.x > this.width) node.vx *= -1;
      if (node.y < 0 || node.y > this.height) node.vy *= -1;

      // Keep in bounds
      node.x = Math.max(0, Math.min(this.width, node.x));
      node.y = Math.max(0, Math.min(this.height, node.y));

      // Update activity
      node.activity = (node.activity + 0.01) % 1;

      // Mouse interaction
      const mouseDist = this.getDistanceToMouse(node.x, node.y);
      const mouseInfluence = mouseDist < 150 ? (1 - mouseDist / 150) : 0;

      // Draw node
      const radius = node.radius * (1 + mouseInfluence * 0.5);
      const brightness = 0.5 + Math.sin(node.activity * Math.PI * 2) * 0.3 + mouseInfluence * 0.5;

      this.ctx.fillStyle = `rgba(129, 140, 248, ${brightness})`;
      this.ctx.beginPath();
      this.ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
      this.ctx.fill();

      // Glow effect
      const gradient = this.ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, radius * 3
      );
      gradient.addColorStop(0, `rgba(129, 140, 248, ${brightness * 0.5})`);
      gradient.addColorStop(1, 'rgba(129, 140, 248, 0)');

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(node.x, node.y, radius * 3, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Create random pulses
    if (Math.random() < 0.02) {
      const randomNode = this.nodes[Math.floor(Math.random() * this.nodes.length)];
      this.createPulse(randomNode);
    }

    // Update and draw pulses
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const pulse = this.pulses[i];
      pulse.radius += 2;
      pulse.opacity -= 0.01;

      if (pulse.opacity <= 0) {
        this.pulses.splice(i, 1);
        continue;
      }

      this.ctx.strokeStyle = `rgba(255, 255, 255, ${pulse.opacity})`;
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
      this.ctx.stroke();
    }
  }

  createPulse(node) {
    this.pulses.push({
      x: node.x,
      y: node.y,
      radius: 0,
      opacity: 0.5
    });
  }

  getDistanceToMouse(x, y) {
    const dx = x - this.mouseX;
    const dy = y - this.mouseY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}

/**
 * Particle Universe Animation
 * Thousands of particles forming galaxies with gravitational effects
 */
class ParticleUniverseAnimation extends BaseAnimation {
  constructor() {
    super();
    this.particles = [];
    this.galaxyCenters = [];
    this.starField = [];
  }

  init(container) {
    super.init(container);
    this.generateUniverse();
  }

  generateUniverse() {
    // Create background star field
    for (let i = 0; i < 200; i++) {
      this.starField.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size: Math.random() * 2,
        brightness: Math.random()
      });
    }

    // Create galaxy centers
    const galaxyCount = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < galaxyCount; i++) {
      this.galaxyCenters.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        mass: 50 + Math.random() * 100,
        rotation: Math.random() * Math.PI * 2
      });
    }

    // Create particles around galaxy centers
    const particlesPerGalaxy = Math.min(300, Math.floor(this.width * this.height / 5000));

    for (const galaxy of this.galaxyCenters) {
      for (let i = 0; i < particlesPerGalaxy; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 200 + 50;
        const orbitSpeed = 0.001 + Math.random() * 0.003;

        this.particles.push({
          x: galaxy.x + Math.cos(angle) * distance,
          y: galaxy.y + Math.sin(angle) * distance,
          vx: 0,
          vy: 0,
          size: Math.random() * 2 + 1,
          color: this.getParticleColor(),
          orbitAngle: angle,
          orbitDistance: distance,
          orbitSpeed: orbitSpeed,
          galaxy: galaxy
        });
      }
    }
  }

  getParticleColor() {
    const colors = [
      { r: 255, g: 200, b: 100 }, // Yellow
      { r: 255, g: 150, b: 150 }, // Red
      { r: 150, g: 150, b: 255 }, // Blue
      { r: 255, g: 255, b: 255 }, // White
      { r: 200, g: 150, b: 255 }  // Purple
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  render() {
    // Clear with deep space color
    this.ctx.fillStyle = 'rgba(5, 5, 15, 0.1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Draw star field
    for (const star of this.starField) {
      const twinkle = Math.sin(this.time * star.brightness * 10) * 0.5 + 0.5;
      this.ctx.fillStyle = `rgba(255, 255, 255, ${twinkle * 0.8})`;
      this.ctx.fillRect(star.x, star.y, star.size, star.size);
    }

    // Update galaxy rotation
    for (const galaxy of this.galaxyCenters) {
      galaxy.rotation += 0.001;

      // Slow drift
      galaxy.x += Math.sin(this.time * 0.1) * 0.1;
      galaxy.y += Math.cos(this.time * 0.1) * 0.1;
    }

    // Update and draw particles
    for (const particle of this.particles) {
      // Orbital motion
      particle.orbitAngle += particle.orbitSpeed;

      // Spiral effect
      const spiralFactor = Math.sin(particle.orbitAngle * 2) * 10;
      const actualDistance = particle.orbitDistance + spiralFactor;

      particle.x = particle.galaxy.x + Math.cos(particle.orbitAngle + particle.galaxy.rotation) * actualDistance;
      particle.y = particle.galaxy.y + Math.sin(particle.orbitAngle + particle.galaxy.rotation) * actualDistance;

      // Mouse gravity
      const mouseDx = this.mouseX - particle.x;
      const mouseDy = this.mouseY - particle.y;
      const mouseDistance = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

      if (mouseDistance < 200 && mouseDistance > 10) {
        const force = 2 / mouseDistance;
        particle.x += mouseDx * force;
        particle.y += mouseDy * force;
      }

      // Draw particle with glow
      const gradient = this.ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.size * 3
      );

      gradient.addColorStop(0, `rgba(${particle.color.r}, ${particle.color.g}, ${particle.color.b}, 1)`);
      gradient.addColorStop(0.5, `rgba(${particle.color.r}, ${particle.color.g}, ${particle.color.b}, 0.5)`);
      gradient.addColorStop(1, `rgba(${particle.color.r}, ${particle.color.g}, ${particle.color.b}, 0)`);

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Draw galaxy centers
    for (const galaxy of this.galaxyCenters) {
      const gradient = this.ctx.createRadialGradient(
        galaxy.x, galaxy.y, 0,
        galaxy.x, galaxy.y, galaxy.mass
      );

      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
      gradient.addColorStop(0.3, 'rgba(150, 150, 255, 0.4)');
      gradient.addColorStop(1, 'rgba(100, 100, 200, 0)');

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(galaxy.x, galaxy.y, galaxy.mass, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }
}

/**
 * Liquid Metal Animation
 * Metallic blob that flows and responds to mouse
 */
class LiquidMetalAnimation extends BaseAnimation {
  constructor() {
    super();
    this.blobs = [];
    this.metaballs = [];
  }

  init(container) {
    super.init(container);
    this.canvas.style.filter = 'contrast(20) brightness(1.2)';
    this.generateBlobs();
  }

  generateBlobs() {
    const blobCount = 8;

    for (let i = 0; i < blobCount; i++) {
      this.blobs.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        radius: 30 + Math.random() * 50,
        phase: Math.random() * Math.PI * 2
      });
    }
  }

  render() {
    // Dark background
    this.ctx.fillStyle = 'rgba(10, 10, 10, 1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update blobs
    for (const blob of this.blobs) {
      // Organic movement
      blob.vx += (Math.random() - 0.5) * 0.5;
      blob.vy += (Math.random() - 0.5) * 0.5;

      // Limit speed
      blob.vx *= 0.98;
      blob.vy *= 0.98;

      // Move
      blob.x += blob.vx;
      blob.y += blob.vy;

      // Bounce off walls
      if (blob.x < blob.radius || blob.x > this.width - blob.radius) {
        blob.vx *= -0.8;
        blob.x = Math.max(blob.radius, Math.min(this.width - blob.radius, blob.x));
      }
      if (blob.y < blob.radius || blob.y > this.height - blob.radius) {
        blob.vy *= -0.8;
        blob.y = Math.max(blob.radius, Math.min(this.height - blob.radius, blob.y));
      }

      // Mouse attraction
      const dx = this.mouseX - blob.x;
      const dy = this.mouseY - blob.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 300 && distance > 50) {
        blob.vx += (dx / distance) * 0.5;
        blob.vy += (dy / distance) * 0.5;
      }

      // Pulsing
      blob.phase += 0.02;
      const pulseFactor = 1 + Math.sin(blob.phase) * 0.1;
      const currentRadius = blob.radius * pulseFactor;

      // Draw metallic blob
      const gradient = this.ctx.createRadialGradient(
        blob.x - currentRadius * 0.3,
        blob.y - currentRadius * 0.3,
        0,
        blob.x,
        blob.y,
        currentRadius
      );

      gradient.addColorStop(0, 'rgba(220, 220, 255, 0.9)');
      gradient.addColorStop(0.5, 'rgba(150, 150, 200, 0.6)');
      gradient.addColorStop(1, 'rgba(100, 100, 150, 0.3)');

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(blob.x, blob.y, currentRadius, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Add mouse blob
    const mouseGradient = this.ctx.createRadialGradient(
      this.mouseX - 20,
      this.mouseY - 20,
      0,
      this.mouseX,
      this.mouseY,
      60
    );

    mouseGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
    mouseGradient.addColorStop(0.5, 'rgba(200, 200, 255, 0.5)');
    mouseGradient.addColorStop(1, 'rgba(150, 150, 200, 0.2)');

    this.ctx.fillStyle = mouseGradient;
    this.ctx.beginPath();
    this.ctx.arc(this.mouseX, this.mouseY, 60, 0, Math.PI * 2);
    this.ctx.fill();
  }
}

/**
 * Blood Cells Animation
 * Realistic blood flow with red and white blood cells
 */
class BloodCellsAnimation extends BaseAnimation {
  constructor() {
    super();
    this.redCells = [];
    this.whiteCells = [];
    this.platelets = [];
    this.flowField = [];
  }

  init(container) {
    super.init(container);
    this.generateCells();
    this.generateFlowField();
  }

  generateCells() {
    // Red blood cells
    const redCellCount = Math.min(150, Math.floor(this.width * this.height / 8000));
    for (let i = 0; i < redCellCount; i++) {
      this.redCells.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: 0,
        vy: 0,
        radius: 15 + Math.random() * 10,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.05,
        deformation: Math.random() * 0.3 + 0.7
      });
    }

    // White blood cells
    const whiteCellCount = Math.floor(redCellCount / 10);
    for (let i = 0; i < whiteCellCount; i++) {
      this.whiteCells.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: 0,
        vy: 0,
        radius: 20 + Math.random() * 10,
        pseudopods: []
      });
    }

    // Platelets
    const plateletCount = Math.floor(redCellCount / 5);
    for (let i = 0; i < plateletCount; i++) {
      this.platelets.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: 0,
        vy: 0,
        radius: 5 + Math.random() * 3
      });
    }
  }

  generateFlowField() {
    const cols = Math.ceil(this.width / 50);
    const rows = Math.ceil(this.height / 50);

    for (let y = 0; y < rows; y++) {
      this.flowField[y] = [];
      for (let x = 0; x < cols; x++) {
        const angle = (Math.sin(x * 0.1) + Math.cos(y * 0.1)) * Math.PI;
        const force = 0.5 + Math.sin(x * 0.05 + y * 0.05) * 0.3;
        this.flowField[y][x] = {
          angle: angle,
          force: force
        };
      }
    }
  }

  render() {
    // Blood-like background
    this.ctx.fillStyle = 'rgba(40, 0, 0, 0.1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update flow field
    for (let y = 0; y < this.flowField.length; y++) {
      for (let x = 0; x < this.flowField[y].length; x++) {
        this.flowField[y][x].angle += Math.sin(this.time + x * 0.1 + y * 0.1) * 0.01;
      }
    }

    // Draw and update red blood cells
    for (const cell of this.redCells) {
      // Get flow field influence
      const gridX = Math.floor(cell.x / 50);
      const gridY = Math.floor(cell.y / 50);

      if (this.flowField[gridY] && this.flowField[gridY][gridX]) {
        const flow = this.flowField[gridY][gridX];
        cell.vx += Math.cos(flow.angle) * flow.force * 0.1;
        cell.vy += Math.sin(flow.angle) * flow.force * 0.1;
      }

      // Damping
      cell.vx *= 0.98;
      cell.vy *= 0.98;

      // Update position
      cell.x += cell.vx;
      cell.y += cell.vy;

      // Wrap around edges
      if (cell.x < -cell.radius) cell.x = this.width + cell.radius;
      if (cell.x > this.width + cell.radius) cell.x = -cell.radius;
      if (cell.y < -cell.radius) cell.y = this.height + cell.radius;
      if (cell.y > this.height + cell.radius) cell.y = -cell.radius;

      // Update rotation
      cell.rotation += cell.rotationSpeed;

      // Draw red blood cell (donut shape)
      this.ctx.save();
      this.ctx.translate(cell.x, cell.y);
      this.ctx.rotate(cell.rotation);
      this.ctx.scale(1, cell.deformation);

      // Outer circle
      const gradient = this.ctx.createRadialGradient(0, 0, cell.radius * 0.3, 0, 0, cell.radius);
      gradient.addColorStop(0, 'rgba(120, 20, 20, 0.3)');
      gradient.addColorStop(0.5, 'rgba(180, 30, 30, 0.8)');
      gradient.addColorStop(1, 'rgba(220, 40, 40, 0.9)');

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(0, 0, cell.radius, 0, Math.PI * 2);
      this.ctx.fill();

      // Inner depression
      this.ctx.fillStyle = 'rgba(80, 10, 10, 0.5)';
      this.ctx.beginPath();
      this.ctx.arc(0, 0, cell.radius * 0.3, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.restore();
    }

    // Draw white blood cells
    for (const cell of this.whiteCells) {
      // Similar flow field influence
      const gridX = Math.floor(cell.x / 50);
      const gridY = Math.floor(cell.y / 50);

      if (this.flowField[gridY] && this.flowField[gridY][gridX]) {
        const flow = this.flowField[gridY][gridX];
        cell.vx += Math.cos(flow.angle) * flow.force * 0.05;
        cell.vy += Math.sin(flow.angle) * flow.force * 0.05;
      }

      cell.vx *= 0.95;
      cell.vy *= 0.95;

      cell.x += cell.vx;
      cell.y += cell.vy;

      // Wrap around
      if (cell.x < -cell.radius) cell.x = this.width + cell.radius;
      if (cell.x > this.width + cell.radius) cell.x = -cell.radius;
      if (cell.y < -cell.radius) cell.y = this.height + cell.radius;
      if (cell.y > this.height + cell.radius) cell.y = -cell.radius;

      // Draw white blood cell with pseudopods
      this.ctx.fillStyle = 'rgba(240, 240, 255, 0.7)';
      this.ctx.beginPath();

      const points = 8;
      for (let i = 0; i < points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const wobble = Math.sin(this.time * 2 + i) * 5;
        const r = cell.radius + wobble;
        const x = cell.x + Math.cos(angle) * r;
        const y = cell.y + Math.sin(angle) * r;

        if (i === 0) {
          this.ctx.moveTo(x, y);
        } else {
          this.ctx.lineTo(x, y);
        }
      }

      this.ctx.closePath();
      this.ctx.fill();

      // Nucleus
      this.ctx.fillStyle = 'rgba(180, 180, 220, 0.8)';
      this.ctx.beginPath();
      this.ctx.arc(cell.x, cell.y, cell.radius * 0.4, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Draw platelets
    for (const platelet of this.platelets) {
      const gridX = Math.floor(platelet.x / 50);
      const gridY = Math.floor(platelet.y / 50);

      if (this.flowField[gridY] && this.flowField[gridY][gridX]) {
        const flow = this.flowField[gridY][gridX];
        platelet.vx += Math.cos(flow.angle) * flow.force * 0.2;
        platelet.vy += Math.sin(flow.angle) * flow.force * 0.2;
      }

      platelet.vx *= 0.99;
      platelet.vy *= 0.99;

      platelet.x += platelet.vx;
      platelet.y += platelet.vy;

      // Wrap around
      if (platelet.x < -platelet.radius) platelet.x = this.width + platelet.radius;
      if (platelet.x > this.width + platelet.radius) platelet.x = -platelet.radius;
      if (platelet.y < -platelet.radius) platelet.y = this.height + platelet.radius;
      if (platelet.y > this.height + platelet.radius) platelet.y = -platelet.radius;

      // Draw platelet
      this.ctx.fillStyle = 'rgba(255, 200, 150, 0.6)';
      this.ctx.beginPath();
      this.ctx.arc(platelet.x, platelet.y, platelet.radius, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }
}

/**
 * Cyber Rain Animation (Matrix-style)
 */
class CyberRainAnimation extends BaseAnimation {
  constructor() {
    super();
    this.columns = [];
    this.characters = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    this.columnWidth = 20;
  }

  init(container) {
    super.init(container);
    this.ctx = this.canvas.getContext('2d');
    this.setupColumns();
  }

  setupColumns() {
    const columnCount = Math.ceil(this.width / this.columnWidth);

    for (let i = 0; i < columnCount; i++) {
      this.columns.push({
        x: i * this.columnWidth,
        drops: []
      });

      // Initialize drops
      const dropCount = 1 + Math.floor(Math.random() * 3);
      for (let j = 0; j < dropCount; j++) {
        this.columns[i].drops.push({
          y: -Math.random() * this.height,
          speed: 5 + Math.random() * 10,
          length: 10 + Math.floor(Math.random() * 20),
          chars: []
        });
      }
    }
  }

  render() {
    // Dark fade effect
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    this.ctx.font = '14px monospace';

    for (const column of this.columns) {
      for (const drop of column.drops) {
        // Update drop position
        drop.y += drop.speed;

        // Reset drop when it goes off screen
        if (drop.y - drop.length * 20 > this.height) {
          drop.y = -drop.length * 20;
          drop.speed = 5 + Math.random() * 10;
          drop.length = 10 + Math.floor(Math.random() * 20);
        }

        // Draw characters
        for (let i = 0; i < drop.length; i++) {
          const charY = drop.y - i * 20;

          if (charY < 0 || charY > this.height) continue;

          // Character changes
          if (Math.random() < 0.1 || !drop.chars[i]) {
            drop.chars[i] = this.characters[Math.floor(Math.random() * this.characters.length)];
          }

          // Color based on position in drop
          let alpha, color;
          if (i === 0) {
            // Head of the drop - white
            color = '255, 255, 255';
            alpha = 1;
          } else if (i < 3) {
            // Near head - bright green
            color = '100, 255, 100';
            alpha = 1 - (i / 10);
          } else {
            // Tail - fading green
            color = '0, 255, 0';
            alpha = Math.max(0, 1 - (i / drop.length));
          }

          // Mouse interaction - brighten nearby characters
          const dist = Math.sqrt(
            Math.pow(column.x + this.columnWidth / 2 - this.mouseX, 2) +
                        Math.pow(charY - this.mouseY, 2)
          );

          if (dist < 100) {
            alpha = Math.min(1, alpha + (1 - dist / 100) * 0.5);
            if (dist < 50) {
              color = '150, 255, 255'; // Cyan when very close to mouse
            }
          }

          this.ctx.fillStyle = `rgba(${color}, ${alpha})`;
          this.ctx.fillText(drop.chars[i], column.x, charY);

          // Glow effect for bright characters
          if (alpha > 0.8) {
            this.ctx.shadowColor = `rgba(${color}, ${alpha})`;
            this.ctx.shadowBlur = 10;
            this.ctx.fillText(drop.chars[i], column.x, charY);
            this.ctx.shadowBlur = 0;
          }
        }
      }
    }
  }
}

/**
 * Quantum Field Animation
 * Particle-wave duality visualization with quantum fluctuations
 */
class QuantumFieldAnimation extends BaseAnimation {
  constructor() {
    super();
    this.field = [];
    this.waves = [];
    this.particles = [];
    this.gridSize = 30;
  }

  init(container) {
    super.init(container);
    this.generateField();
  }

  generateField() {
    const cols = Math.ceil(this.width / this.gridSize);
    const rows = Math.ceil(this.height / this.gridSize);

    // Initialize field grid
    for (let y = 0; y < rows; y++) {
      this.field[y] = [];
      for (let x = 0; x < cols; x++) {
        this.field[y][x] = {
          energy: 0,
          phase: Math.random() * Math.PI * 2,
          frequency: 0.01 + Math.random() * 0.02
        };
      }
    }

    // Create wave sources
    for (let i = 0; i < 3; i++) {
      this.waves.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        amplitude: 50 + Math.random() * 50,
        frequency: 0.02 + Math.random() * 0.03,
        phase: 0
      });
    }

    // Create quantum particles
    for (let i = 0; i < 50; i++) {
      this.particles.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        waveFunction: Math.random() * Math.PI * 2,
        collapsed: false,
        probability: Math.random()
      });
    }
  }

  render() {
    // Deep quantum void background
    this.ctx.fillStyle = 'rgba(0, 5, 10, 0.1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update wave sources
    for (const wave of this.waves) {
      wave.phase += wave.frequency;

      // Slow movement
      wave.x += Math.sin(wave.phase * 0.5) * 0.5;
      wave.y += Math.cos(wave.phase * 0.3) * 0.5;
    }

    // Calculate field energy at each point
    const cols = Math.ceil(this.width / this.gridSize);
    const rows = Math.ceil(this.height / this.gridSize);

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const px = x * this.gridSize;
        const py = y * this.gridSize;

        let totalEnergy = 0;

        // Superposition of waves
        for (const wave of this.waves) {
          const distance = Math.sqrt(
            Math.pow(px - wave.x, 2) +
                        Math.pow(py - wave.y, 2)
          );

          const waveEnergy = (wave.amplitude / (1 + distance * 0.01)) *
                                      Math.sin(distance * 0.02 - wave.phase);
          totalEnergy += waveEnergy;
        }

        // Mouse interaction - observer effect
        const mouseDist = Math.sqrt(
          Math.pow(px - this.mouseX, 2) +
                    Math.pow(py - this.mouseY, 2)
        );

        if (mouseDist < 150) {
          const observerEffect = (1 - mouseDist / 150) * 100;
          totalEnergy += observerEffect * Math.sin(this.time * 10);
        }

        // Quantum fluctuations
        totalEnergy += Math.sin(this.field[y][x].phase) * 10;
        this.field[y][x].phase += this.field[y][x].frequency;

        this.field[y][x].energy = totalEnergy;

        // Draw field point
        const intensity = Math.abs(totalEnergy) / 100;
        const hue = 180 + totalEnergy * 0.5;
        const saturation = 80 + intensity * 20;
        const lightness = 10 + intensity * 40;

        this.ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${intensity * 0.5})`;
        this.ctx.fillRect(px - this.gridSize / 2, py - this.gridSize / 2, this.gridSize, this.gridSize);
      }
    }

    // Draw field lines
    this.ctx.strokeStyle = 'rgba(100, 150, 255, 0.2)';
    this.ctx.lineWidth = 1;

    for (let y = 0; y < rows - 1; y++) {
      for (let x = 0; x < cols - 1; x++) {
        const energy = this.field[y][x].energy;

        if (Math.abs(energy) > 30) {
          const px = x * this.gridSize;
          const py = y * this.gridSize;

          // Connect to neighboring high energy points
          if (Math.abs(this.field[y][x + 1].energy) > 30) {
            this.ctx.beginPath();
            this.ctx.moveTo(px, py);
            this.ctx.lineTo(px + this.gridSize, py);
            this.ctx.stroke();
          }

          if (Math.abs(this.field[y + 1][x].energy) > 30) {
            this.ctx.beginPath();
            this.ctx.moveTo(px, py);
            this.ctx.lineTo(px, py + this.gridSize);
            this.ctx.stroke();
          }
        }
      }
    }

    // Update and draw quantum particles
    for (const particle of this.particles) {
      // Wave function evolution
      particle.waveFunction += 0.1;

      // Probability cloud size
      const cloudSize = 20 + Math.sin(particle.waveFunction) * 10;

      // Check for observation (mouse proximity)
      const mouseDist = Math.sqrt(
        Math.pow(particle.x - this.mouseX, 2) +
                Math.pow(particle.y - this.mouseY, 2)
      );

      if (mouseDist < 100) {
        // Collapse wave function
        particle.collapsed = true;
        particle.probability = 1;
      } else if (particle.collapsed) {
        // Gradual return to superposition
        particle.probability *= 0.99;
        if (particle.probability < 0.1) {
          particle.collapsed = false;
        }
      } else {
        particle.probability = 0.5 + Math.sin(particle.waveFunction) * 0.3;
      }

      // Movement
      particle.vx += (Math.random() - 0.5) * 0.5;
      particle.vy += (Math.random() - 0.5) * 0.5;
      particle.vx *= 0.99;
      particle.vy *= 0.99;

      particle.x += particle.vx;
      particle.y += particle.vy;

      // Wrap around
      if (particle.x < 0) particle.x = this.width;
      if (particle.x > this.width) particle.x = 0;
      if (particle.y < 0) particle.y = this.height;
      if (particle.y > this.height) particle.y = 0;

      // Draw particle
      if (particle.collapsed) {
        // Collapsed state - solid particle
        this.ctx.fillStyle = `rgba(255, 255, 255, ${particle.probability})`;
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, 3, 0, Math.PI * 2);
        this.ctx.fill();
      } else {
        // Superposition - probability cloud
        const gradient = this.ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, cloudSize
        );

        gradient.addColorStop(0, `rgba(100, 150, 255, ${particle.probability})`);
        gradient.addColorStop(0.5, `rgba(150, 100, 255, ${particle.probability * 0.5})`);
        gradient.addColorStop(1, 'rgba(100, 100, 200, 0)');

        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, cloudSize, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
  }
}

/**
 * Horror Tentacles Animation
 * Lovecraftian tentacles emerging from darkness
 */
class HorrorTentaclesAnimation extends BaseAnimation {
  constructor() {
    super();
    this.tentacles = [];
    this.eyes = [];
    this.mist = [];
  }

  init(container) {
    super.init(container);
    this.generateTentacles();
    this.generateEyes();
  }

  generateTentacles() {
    const tentacleCount = 5 + Math.floor(Math.random() * 3);

    for (let i = 0; i < tentacleCount; i++) {
      const segments = 20 + Math.floor(Math.random() * 10);
      const tentacle = {
        base: {
          x: Math.random() * this.width,
          y: this.height + 50
        },
        segments: [],
        thickness: 30 + Math.random() * 20,
        sway: Math.random() * Math.PI * 2,
        swaySpeed: 0.01 + Math.random() * 0.02,
        reaching: false,
        targetX: 0,
        targetY: 0
      };

      // Initialize segments
      for (let j = 0; j < segments; j++) {
        tentacle.segments.push({
          x: tentacle.base.x,
          y: tentacle.base.y - j * 20,
          angle: -Math.PI / 2 + (Math.random() - 0.5) * 0.5
        });
      }

      this.tentacles.push(tentacle);
    }
  }

  generateEyes() {
    const eyeCount = 10 + Math.floor(Math.random() * 10);

    for (let i = 0; i < eyeCount; i++) {
      this.eyes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        radius: 5 + Math.random() * 10,
        blinkTimer: Math.random() * 100,
        pupilX: 0,
        pupilY: 0,
        open: true
      });
    }
  }

  render() {
    // Dark horror background
    this.ctx.fillStyle = 'rgba(5, 0, 0, 0.1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Draw dark mist
    const gradient = this.ctx.createLinearGradient(0, 0, 0, this.height);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(0.5, 'rgba(50, 0, 0, 0.1)');
    gradient.addColorStop(1, 'rgba(100, 0, 0, 0.2)');

    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update and draw tentacles
    for (const tentacle of this.tentacles) {
      // Update sway
      tentacle.sway += tentacle.swaySpeed;

      // Check if mouse is near - tentacle reaches for it
      const mouseDistToBase = Math.sqrt(
        Math.pow(tentacle.base.x - this.mouseX, 2) +
                Math.pow(tentacle.base.y - this.mouseY, 2)
      );

      if (mouseDistToBase < 300) {
        tentacle.reaching = true;
        tentacle.targetX = this.mouseX;
        tentacle.targetY = this.mouseY;
      } else {
        tentacle.reaching = false;
      }

      // Update segments using inverse kinematics
      for (let i = tentacle.segments.length - 1; i >= 0; i--) {
        const segment = tentacle.segments[i];

        if (i === tentacle.segments.length - 1 && tentacle.reaching) {
          // Tip segment reaches toward target
          const dx = tentacle.targetX - segment.x;
          const dy = tentacle.targetY - segment.y;
          segment.x += dx * 0.05;
          segment.y += dy * 0.05;
        } else if (i === tentacle.segments.length - 1) {
          // Natural sway for tip
          segment.x = tentacle.base.x + Math.sin(tentacle.sway) * 100;
          segment.y = tentacle.base.y - (i + 1) * 20 + Math.cos(tentacle.sway * 2) * 30;
        } else if (i === 0) {
          // Base segment
          segment.x = tentacle.base.x;
          segment.y = tentacle.base.y;
        } else {
          // Middle segments follow
          const prevSegment = tentacle.segments[i + 1];
          const dx = prevSegment.x - segment.x;
          const dy = prevSegment.y - segment.y;
          const angle = Math.atan2(dy, dx);

          segment.x = prevSegment.x - Math.cos(angle) * 20;
          segment.y = prevSegment.y - Math.sin(angle) * 20;
        }
      }

      // Draw tentacle
      this.ctx.strokeStyle = 'rgba(50, 0, 0, 0.8)';
      this.ctx.lineCap = 'round';

      for (let i = 0; i < tentacle.segments.length - 1; i++) {
        const segment = tentacle.segments[i];
        const nextSegment = tentacle.segments[i + 1];

        // Thickness tapers toward tip
        const thickness = tentacle.thickness * (1 - i / tentacle.segments.length);
        this.ctx.lineWidth = thickness;

        // Main tentacle body
        this.ctx.beginPath();
        this.ctx.moveTo(segment.x, segment.y);
        this.ctx.lineTo(nextSegment.x, nextSegment.y);
        this.ctx.stroke();

        // Suckers
        if (i % 3 === 0 && i > 2) {
          const suckerSize = thickness * 0.3;
          this.ctx.fillStyle = 'rgba(80, 0, 0, 0.6)';
          this.ctx.beginPath();
          this.ctx.arc(segment.x, segment.y, suckerSize, 0, Math.PI * 2);
          this.ctx.fill();

          this.ctx.strokeStyle = 'rgba(100, 0, 0, 0.8)';
          this.ctx.lineWidth = 2;
          this.ctx.stroke();

          this.ctx.strokeStyle = 'rgba(50, 0, 0, 0.8)';
        }
      }
    }

    // Draw eyes in darkness
    for (const eye of this.eyes) {
      // Update blink timer
      eye.blinkTimer--;
      if (eye.blinkTimer <= 0) {
        eye.open = !eye.open;
        eye.blinkTimer = eye.open ? 50 + Math.random() * 150 : 5 + Math.random() * 10;
      }

      // Pupil tracks mouse
      const dx = this.mouseX - eye.x;
      const dy = this.mouseY - eye.y;
      const angle = Math.atan2(dy, dx);
      const distance = Math.min(eye.radius * 0.3, Math.sqrt(dx * dx + dy * dy) * 0.1);

      eye.pupilX = Math.cos(angle) * distance;
      eye.pupilY = Math.sin(angle) * distance;

      if (eye.open) {
        // Eye white (red tinted)
        const eyeGradient = this.ctx.createRadialGradient(
          eye.x, eye.y, 0,
          eye.x, eye.y, eye.radius
        );
        eyeGradient.addColorStop(0, 'rgba(255, 200, 200, 0.9)');
        eyeGradient.addColorStop(0.7, 'rgba(255, 150, 150, 0.7)');
        eyeGradient.addColorStop(1, 'rgba(200, 50, 50, 0.5)');

        this.ctx.fillStyle = eyeGradient;
        this.ctx.beginPath();
        this.ctx.arc(eye.x, eye.y, eye.radius, 0, Math.PI * 2);
        this.ctx.fill();

        // Pupil
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
        this.ctx.beginPath();
        this.ctx.arc(
          eye.x + eye.pupilX,
          eye.y + eye.pupilY,
          eye.radius * 0.4,
          0, Math.PI * 2
        );
        this.ctx.fill();

        // Red glow
        const glowGradient = this.ctx.createRadialGradient(
          eye.x, eye.y, eye.radius,
          eye.x, eye.y, eye.radius * 2
        );
        glowGradient.addColorStop(0, 'rgba(255, 0, 0, 0.2)');
        glowGradient.addColorStop(1, 'rgba(255, 0, 0, 0)');

        this.ctx.fillStyle = glowGradient;
        this.ctx.beginPath();
        this.ctx.arc(eye.x, eye.y, eye.radius * 2, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
  }
}

/**
 * Plasma Storm Animation
 * Dynamic plasma with electromagnetic effects
 */
class PlasmaStormAnimation extends BaseAnimation {
  constructor() {
    super();
    this.plasmaField = [];
    this.lightnings = [];
    this.particles = [];
  }

  init(container) {
    super.init(container);
    this.generatePlasmaField();
    this.generateParticles();
  }

  generatePlasmaField() {
    const resolution = 20;
    const cols = Math.ceil(this.width / resolution);
    const rows = Math.ceil(this.height / resolution);

    for (let y = 0; y < rows; y++) {
      this.plasmaField[y] = [];
      for (let x = 0; x < cols; x++) {
        this.plasmaField[y][x] = {
          charge: (Math.random() - 0.5) * 2,
          phase: Math.random() * Math.PI * 2
        };
      }
    }
  }

  generateParticles() {
    for (let i = 0; i < 100; i++) {
      this.particles.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: (Math.random() - 0.5) * 5,
        vy: (Math.random() - 0.5) * 5,
        charge: Math.random() > 0.5 ? 1 : -1,
        trail: []
      });
    }
  }

  createLightning(x1, y1, x2, y2) {
    const segments = [];
    const steps = 15;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + (x2 - x1) * t + (Math.random() - 0.5) * 30;
      const y = y1 + (y2 - y1) * t + (Math.random() - 0.5) * 30;
      segments.push({ x, y });
    }

    this.lightnings.push({
      segments: segments,
      life: 10,
      maxLife: 10
    });
  }

  render() {
    // Dark stormy background
    this.ctx.fillStyle = 'rgba(0, 0, 10, 0.1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update plasma field
    const resolution = 20;
    const cols = Math.ceil(this.width / resolution);
    const rows = Math.ceil(this.height / resolution);

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const field = this.plasmaField[y][x];
        field.phase += 0.02;
        field.charge = Math.sin(field.phase + x * 0.1 + y * 0.1 + this.time);

        // Mouse influence
        const px = x * resolution;
        const py = y * resolution;
        const mouseDist = Math.sqrt(
          Math.pow(px - this.mouseX, 2) +
                    Math.pow(py - this.mouseY, 2)
        );

        if (mouseDist < 100) {
          field.charge += (1 - mouseDist / 100) * 2;
        }

        // Draw plasma field
        const intensity = Math.abs(field.charge);
        const hue = field.charge > 0 ? 280 : 180; // Purple for positive, cyan for negative

        this.ctx.fillStyle = `hsla(${hue}, 100%, ${30 + intensity * 30}%, ${intensity * 0.3})`;
        this.ctx.fillRect(px - resolution / 2, py - resolution / 2, resolution, resolution);
      }
    }

    // Update and draw particles
    for (const particle of this.particles) {
      // Electromagnetic forces from field
      const gridX = Math.floor(particle.x / resolution);
      const gridY = Math.floor(particle.y / resolution);

      if (this.plasmaField[gridY] && this.plasmaField[gridY][gridX]) {
        const field = this.plasmaField[gridY][gridX];
        const force = field.charge * particle.charge * 0.5;

        particle.vx += Math.cos(field.phase) * force;
        particle.vy += Math.sin(field.phase) * force;
      }

      // Damping and limits
      particle.vx *= 0.98;
      particle.vy *= 0.98;
      particle.vx = Math.max(-10, Math.min(10, particle.vx));
      particle.vy = Math.max(-10, Math.min(10, particle.vy));

      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Wrap around
      if (particle.x < 0) particle.x = this.width;
      if (particle.x > this.width) particle.x = 0;
      if (particle.y < 0) particle.y = this.height;
      if (particle.y > this.height) particle.y = 0;

      // Update trail
      particle.trail.push({ x: particle.x, y: particle.y });
      if (particle.trail.length > 20) {
        particle.trail.shift();
      }

      // Draw trail
      for (let i = 0; i < particle.trail.length - 1; i++) {
        const alpha = i / particle.trail.length;
        const color = particle.charge > 0 ? '255, 100, 255' : '100, 200, 255';

        this.ctx.strokeStyle = `rgba(${color}, ${alpha * 0.5})`;
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.ctx.moveTo(particle.trail[i].x, particle.trail[i].y);
        this.ctx.lineTo(particle.trail[i + 1].x, particle.trail[i + 1].y);
        this.ctx.stroke();
      }

      // Draw particle
      const color = particle.charge > 0 ? '255, 150, 255' : '150, 200, 255';
      const gradient = this.ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, 10
      );
      gradient.addColorStop(0, `rgba(${color}, 1)`);
      gradient.addColorStop(0.5, `rgba(${color}, 0.5)`);
      gradient.addColorStop(1, `rgba(${color}, 0)`);

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, 10, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Random lightning
    if (Math.random() < 0.05) {
      const x1 = Math.random() * this.width;
      const y1 = Math.random() * this.height / 2;
      const x2 = x1 + (Math.random() - 0.5) * 200;
      const y2 = y1 + Math.random() * 200;

      this.createLightning(x1, y1, x2, y2);
    }

    // Mouse lightning
    if (Math.random() < 0.1) {
      const nearbyParticle = this.particles[Math.floor(Math.random() * this.particles.length)];
      const dist = Math.sqrt(
        Math.pow(nearbyParticle.x - this.mouseX, 2) +
                Math.pow(nearbyParticle.y - this.mouseY, 2)
      );

      if (dist < 200) {
        this.createLightning(this.mouseX, this.mouseY, nearbyParticle.x, nearbyParticle.y);
      }
    }

    // Draw lightning
    for (let i = this.lightnings.length - 1; i >= 0; i--) {
      const lightning = this.lightnings[i];
      lightning.life--;

      if (lightning.life <= 0) {
        this.lightnings.splice(i, 1);
        continue;
      }

      const alpha = lightning.life / lightning.maxLife;

      // Main bolt
      this.ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
      this.ctx.lineWidth = 2;
      this.ctx.shadowBlur = 20;
      this.ctx.shadowColor = 'rgba(150, 150, 255, 1)';

      this.ctx.beginPath();
      for (let j = 0; j < lightning.segments.length; j++) {
        const segment = lightning.segments[j];
        if (j === 0) {
          this.ctx.moveTo(segment.x, segment.y);
        } else {
          this.ctx.lineTo(segment.x, segment.y);
        }
      }
      this.ctx.stroke();

      // Glow
      this.ctx.strokeStyle = `rgba(150, 150, 255, ${alpha * 0.5})`;
      this.ctx.lineWidth = 6;
      this.ctx.stroke();

      this.ctx.shadowBlur = 0;
    }
  }
}

/**
 * DNA Helix Animation
 * Double helix structure with genetic data visualization
 */
class DNAHelixAnimation extends BaseAnimation {
  constructor() {
    super();
    this.helixPoints = [];
    this.basePairs = [];
    this.rotation = 0;
    this.geneticData = [];
  }

  init(container) {
    super.init(container);
    this.generateHelix();
    this.generateGeneticData();
  }

  generateHelix() {
    const segments = 50;
    const height = this.height * 1.5;

    for (let i = 0; i < segments; i++) {
      const t = i / segments;
      const y = -height * 0.25 + t * height;

      this.helixPoints.push({
        t: t,
        y: y,
        phase: t * Math.PI * 4
      });

      // Base pairs
      if (i % 2 === 0) {
        const bases = ['A', 'T', 'G', 'C'];
        const base1 = bases[Math.floor(Math.random() * bases.length)];
        let base2;

        // Complementary base pairing
        switch (base1) {
          case 'A': base2 = 'T'; break;
          case 'T': base2 = 'A'; break;
          case 'G': base2 = 'C'; break;
          case 'C': base2 = 'G'; break;
        }

        this.basePairs.push({
          y: y,
          base1: base1,
          base2: base2,
          highlight: Math.random() < 0.1
        });
      }
    }
  }

  generateGeneticData() {
    // Simulated genetic sequence visualization
    for (let i = 0; i < 20; i++) {
      this.geneticData.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        sequence: this.generateSequence(),
        opacity: Math.random(),
        speed: 0.5 + Math.random() * 0.5
      });
    }
  }

  generateSequence() {
    const bases = ['A', 'T', 'G', 'C'];
    let sequence = '';
    for (let i = 0; i < 10; i++) {
      sequence += bases[Math.floor(Math.random() * bases.length)];
    }
    return sequence;
  }

  render() {
    // Scientific background
    this.ctx.fillStyle = 'rgba(0, 10, 20, 0.1)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update rotation
    this.rotation += 0.01;

    // Center of screen
    const centerX = this.width / 2;
    const centerY = this.height / 2;

    // Draw background genetic data
    this.ctx.font = '10px monospace';
    for (const data of this.geneticData) {
      data.y -= data.speed;
      if (data.y < -50) {
        data.y = this.height + 50;
        data.sequence = this.generateSequence();
      }

      data.opacity = 0.1 + Math.sin(this.time * 2 + data.x) * 0.1;

      this.ctx.fillStyle = `rgba(0, 255, 100, ${data.opacity})`;
      this.ctx.fillText(data.sequence, data.x, data.y);
    }

    // Draw DNA helix
    const radius = 100;

    // Draw base pairs first (behind strands)
    for (const pair of this.basePairs) {
      const phase1 = this.rotation + pair.y * 0.01;
      const phase2 = phase1 + Math.PI;

      const x1 = centerX + Math.cos(phase1) * radius;
      const z1 = Math.sin(phase1) * radius;
      const x2 = centerX + Math.cos(phase2) * radius;
      const z2 = Math.sin(phase2) * radius;

      // 3D perspective
      const scale1 = 1 + z1 / 200;
      const scale2 = 1 + z2 / 200;

      // Draw connecting line
      const alpha = pair.highlight ? 0.8 : 0.3;
      this.ctx.strokeStyle = `rgba(100, 255, 100, ${alpha})`;
      this.ctx.lineWidth = pair.highlight ? 3 : 1;

      this.ctx.beginPath();
      this.ctx.moveTo(x1, pair.y);
      this.ctx.lineTo(x2, pair.y);
      this.ctx.stroke();

      // Draw base labels
      if (pair.highlight) {
        this.ctx.font = 'bold 14px monospace';
        this.ctx.fillStyle = this.getBaseColor(pair.base1, 0.9);
        this.ctx.fillText(pair.base1, x1 - 7, pair.y + 5);

        this.ctx.fillStyle = this.getBaseColor(pair.base2, 0.9);
        this.ctx.fillText(pair.base2, x2 - 7, pair.y + 5);
      }
    }

    // Draw double helix strands
    for (let strand = 0; strand < 2; strand++) {
      const phaseOffset = strand * Math.PI;

      this.ctx.beginPath();

      for (let i = 0; i < this.helixPoints.length; i++) {
        const point = this.helixPoints[i];
        const phase = this.rotation + point.phase + phaseOffset;

        const x = centerX + Math.cos(phase) * radius;
        const z = Math.sin(phase) * radius;

        // 3D perspective effect
        const scale = 1 + z / 200;
        const alpha = 0.5 + z / 200;

        // Mouse interaction - highlight nearby segments
        const mouseDist = Math.sqrt(
          Math.pow(x - this.mouseX, 2) +
                    Math.pow(point.y - this.mouseY, 2)
        );

        const mouseInfluence = mouseDist < 100 ? (1 - mouseDist / 100) : 0;

        if (i === 0) {
          this.ctx.moveTo(x, point.y);
        } else {
          this.ctx.lineTo(x, point.y);
        }

        // Draw node
        const nodeRadius = 3 * scale * (1 + mouseInfluence);
        const gradient = this.ctx.createRadialGradient(
          x, point.y, 0,
          x, point.y, nodeRadius * 3
        );

        const color = strand === 0 ? '100, 150, 255' : '255, 100, 150';
        gradient.addColorStop(0, `rgba(${color}, ${alpha + mouseInfluence})`);
        gradient.addColorStop(1, `rgba(${color}, 0)`);

        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(x, point.y, nodeRadius * 3, 0, Math.PI * 2);
        this.ctx.fill();
      }

      // Draw strand
      const strandColor = strand === 0 ? '100, 150, 255' : '255, 100, 150';
      this.ctx.strokeStyle = `rgba(${strandColor}, 0.6)`;
      this.ctx.lineWidth = 3;
      this.ctx.stroke();

      // Glow effect
      this.ctx.strokeStyle = `rgba(${strandColor}, 0.2)`;
      this.ctx.lineWidth = 10;
      this.ctx.stroke();
    }

    // Draw information panel
    if (Math.abs(this.mouseX - centerX) < 150) {
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      this.ctx.fillRect(50, 50, 200, 100);

      this.ctx.fillStyle = 'rgba(0, 255, 100, 0.9)';
      this.ctx.font = '12px monospace';
      this.ctx.fillText('DNA HELIX VIEWER', 60, 70);
      this.ctx.fillText('Base Pairs: ' + this.basePairs.length, 60, 90);
      this.ctx.fillText('Rotation: ' + this.rotation.toFixed(2), 60, 110);
      this.ctx.fillText('Sequence Active', 60, 130);
    }
  }

  getBaseColor(base, alpha) {
    switch (base) {
      case 'A': return `rgba(255, 100, 100, ${alpha})`; // Red
      case 'T': return `rgba(100, 255, 100, ${alpha})`; // Green
      case 'G': return `rgba(100, 100, 255, ${alpha})`; // Blue
      case 'C': return `rgba(255, 255, 100, ${alpha})`; // Yellow
      default: return `rgba(255, 255, 255, ${alpha})`;
    }
  }
}

/**
 * Fractal Zoom Animation
 * Infinite Mandelbrot set zoom with interactive exploration
 */
class FractalZoomAnimation extends BaseAnimation {
  constructor() {
    super();
    this.zoom = 1;
    this.centerX = -0.5;
    this.centerY = 0;
    this.targetZoom = 1;
    this.targetCenterX = -0.5;
    this.targetCenterY = 0;
    this.maxIterations = 100;
    this.colorOffset = 0;
  }

  init(container) {
    super.init(container);
    // Use 2D context for fractal rendering
  }

  mandelbrot(px, py, maxIter) {
    let x = 0;
    let y = 0;
    let iteration = 0;

    while (x * x + y * y <= 4 && iteration < maxIter) {
      const xTemp = x * x - y * y + px;
      y = 2 * x * y + py;
      x = xTemp;
      iteration++;
    }

    if (iteration === maxIter) {
      return -1;
    }

    // Smooth coloring
    const smoothed = iteration + 1 - Math.log(Math.log(Math.sqrt(x * x + y * y))) / Math.log(2);
    return smoothed;
  }

  render() {
    // Create image data
    const imageData = this.ctx.createImageData(this.width, this.height);
    const data = imageData.data;

    // Smooth transitions
    this.zoom += (this.targetZoom - this.zoom) * 0.1;
    this.centerX += (this.targetCenterX - this.centerX) * 0.1;
    this.centerY += (this.targetCenterY - this.centerY) * 0.1;

    // Auto zoom
    this.targetZoom *= 1.005;
    this.colorOffset += 0.5;

    // Mouse influence
    const mouseXNorm = (this.mouseX / this.width - 0.5) * 2;
    const mouseYNorm = (this.mouseY / this.height - 0.5) * 2;

    this.targetCenterX += mouseXNorm * 0.0001 / this.zoom;
    this.targetCenterY += mouseYNorm * 0.0001 / this.zoom;

    // Calculate visible range
    const range = 3 / this.zoom;
    const minX = this.centerX - range;
    const maxX = this.centerX + range;
    const minY = this.centerY - range;
    const maxY = this.centerY + range;

    // Render fractal
    for (let py = 0; py < this.height; py++) {
      for (let px = 0; px < this.width; px++) {
        const x = minX + (px / this.width) * (maxX - minX);
        const y = minY + (py / this.height) * (maxY - minY);

        const iterations = this.mandelbrot(x, y, this.maxIterations);

        const index = (py * this.width + px) * 4;

        if (iterations === -1) {
          // Inside the set - black
          data[index] = 0;
          data[index + 1] = 0;
          data[index + 2] = 0;
        } else {
          // Outside the set - colorful
          const hue = (iterations * 10 + this.colorOffset) % 360;
          const lightness = 50 + (iterations % 20) * 2.5;
          const rgb = this.hslToRgb(hue / 360, 1, lightness / 100);

          data[index] = rgb.r;
          data[index + 1] = rgb.g;
          data[index + 2] = rgb.b;
        }

        data[index + 3] = 255; // Alpha
      }
    }

    // Draw fractal
    this.ctx.putImageData(imageData, 0, 0);

    // Draw UI overlay
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(10, 10, 200, 80);

    this.ctx.fillStyle = 'rgba(100, 255, 100, 0.9)';
    this.ctx.font = '12px monospace';
    this.ctx.fillText('FRACTAL EXPLORER', 20, 30);
    this.ctx.fillText(`Zoom: ${this.zoom.toExponential(2)}`, 20, 50);
    this.ctx.fillText(`Center: ${this.centerX.toFixed(6)}, ${this.centerY.toFixed(6)}`, 20, 70);
  }

  hslToRgb(h, s, l) {
    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  }

  onMouseMove(e) {
    super.onMouseMove(e);

    // Click to zoom into that area
    if (e.buttons === 1) {
      const mouseXNorm = (e.clientX / this.width - 0.5) * 2;
      const mouseYNorm = (e.clientY / this.height - 0.5) * 2;

      const range = 3 / this.zoom;
      this.targetCenterX = this.centerX + mouseXNorm * range;
      this.targetCenterY = this.centerY + mouseYNorm * range;
      this.targetZoom *= 1.5;
    }
  }
}

/**
 * Blood Drip Animation V5
 * Refined to make paths thicker, trickling more random, and sources dynamic.
 */
class BloodDripAnimation extends BaseAnimation {
  constructor() {
    super();
    this.drips = [];     // Active, moving drips
    this.sources = [];   // Points at the top where drips originate
    this.options = {
      gravity: 0.15,
      trailChance: 0.7,
    };
    this.shadowCanvas = null;
    this.shadowCtx = null;
    this.domMap = []; // Holds the bounding boxes of elements to collide with
  }

  init(container) {
    super.init(container);
    
    this.shadowCanvas = document.createElement('canvas');
    this.shadowCanvas.width = this.width;
    this.shadowCanvas.height = this.height;
    this.shadowCtx = this.shadowCanvas.getContext('2d');
    this.shadowCtx.lineCap = 'round';
    
    this.generateSources();
    this.mapDomElements();
  }
  
  onResize() {
    super.onResize();
    if (this.shadowCanvas) {
        const oldCanvas = this.shadowCanvas;
        this.shadowCanvas = document.createElement('canvas');
        this.shadowCanvas.width = this.width;
        this.shadowCanvas.height = this.height;
        this.shadowCtx = this.shadowCanvas.getContext('2d');
        this.shadowCtx.lineCap = 'round';
        
        if (oldCanvas.width > 0 && oldCanvas.height > 0) {
            this.shadowCtx.drawImage(oldCanvas, 0, 0);
        }
        
        this.generateSources();
        this.mapDomElements();
    }
  }

  mapDomElements() {
      this.domMap = [];
      const selector = '.card-body button, .card-body input, .callout, .file-upload-area, .card-header h2, .stat-card-title, .stat-card-icon, #title-poster-img';
      const elements = this.container.querySelectorAll(selector);
      const containerRect = this.container.getBoundingClientRect();

      elements.forEach(el => {
          const elRect = el.getBoundingClientRect();
          if (elRect.width > 0 && elRect.height > 0) {
              this.domMap.push({
                  left: elRect.left - containerRect.left,
                  top: elRect.top - containerRect.top,
                  right: elRect.right - containerRect.left,
                  bottom: elRect.bottom - containerRect.top,
              });
          }
      });
  }

  generateSources() {
      this.sources = [];
      const numSources = Math.floor(this.width / 80);
      for (let i = 0; i < numSources; i++) {
          this.sources.push({
              x: Math.random() * this.width,
              nextDropIn: 50 + Math.random() * 400,
              color: this.generateRedTone(80, 40)
          });
      }
  }
  
  generateRedTone(base = 130, range = 105) {
    const val = base + (Math.random() * range | 0);
    return `rgb(${val}, 0, 0)`;
  }
  
  createDrip(x, y, color) {
    this.drips.push({
      x: x,
      y: y,
      vy: 0.5 + Math.random() * 2,
      vx: 0,
      size: 1.5 + Math.random() * 2.5,
      color: color,
      leavesTrail: Math.random() < this.options.trailChance,
      lastY: y,
      isTrickling: false,
      trickleBox: null,
      wobble: (Math.random() - 0.5) * 0.5 // For random trickling
    });
  }

  drawDripShape(ctx, x, y, size) {
    const w = size * 1.5;
    const h = size * 2.5;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.bezierCurveTo(x - w, y - h * 0.8, x + w, y - h * 0.8, x, y);
    ctx.fill();
  }

  render() {
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.drawImage(this.shadowCanvas, 0, 0);

    // 1. Update and generate new drips from top sources
    this.sources.forEach(source => {
        // Slowly move the source
        source.x += (Math.random() - 0.5) * 0.3;
        if (source.x < 0) source.x = 0;
        if (source.x > this.width) source.x = this.width;

        source.nextDropIn--;
        if (source.nextDropIn <= 0) {
            this.shadowCtx.fillStyle = source.color;
            this.drawDripShape(this.shadowCtx, source.x, 5, 2);
            this.createDrip(source.x, 5, source.color);
            source.nextDropIn = 100 + Math.random() * 800;
        }
    });

    // 2. Update and draw active drips
    for (let i = this.drips.length - 1; i >= 0; i--) {
        const drip = this.drips[i];
        const lastY = drip.y;
        const lastX = drip.x;

        if (drip.isTrickling) {
            // --- TRICKLING PHYSICS ---
            drip.vy += this.options.gravity * 0.1; // Slower gravity on a surface
            drip.y += drip.vy;

            // Apply horizontal trickle and random wobble
            drip.vx *= 0.95; // Slow down horizontal movement over time
            drip.x += drip.vx + drip.wobble;
            
            const box = drip.trickleBox;
            
            // Keep it on the surface of the element
            if (drip.y > box.top && drip.y < box.bottom) {
                 if (drip.vx < 0) drip.x = Math.max(drip.x, box.left);
                 if (drip.vx > 0) drip.x = Math.min(drip.x, box.right);
            }
            
            // Release if it has trickled past the bottom
            if (drip.y >= box.bottom) {
                drip.isTrickling = false;
                drip.trickleBox = null;
                drip.vx = 0;
            }

        } else {
            // --- NORMAL FALLING PHYSICS ---
            drip.vy += this.options.gravity;
            drip.y += drip.vy;

            // Check for collision with DOM elements
            for (const box of this.domMap) {
                if (drip.x > box.left && drip.x < box.right && (drip.y + drip.vy) > box.top && drip.y < box.top + 10) {
                    drip.y = box.top;
                    drip.vy = 0;
                    drip.isTrickling = true;
                    drip.trickleBox = box;
                    
                    // Decide which way to trickle based on which side is closer
                    const middle = box.left + box.right / 2;
                    drip.vx = (drip.x < middle) ? -0.3 - Math.random() * 0.3 : 0.3 + Math.random() * 0.3;
                    break;
                }
            }
        }

        // Draw the moving drip head
        this.ctx.fillStyle = drip.color;
        this.drawDripShape(this.ctx, drip.x, drip.y, drip.size);
        
        // Leave a thicker trail
        if (drip.leavesTrail) {
            this.shadowCtx.strokeStyle = drip.color;
            this.shadowCtx.globalAlpha = 0.4;
            this.shadowCtx.lineWidth = drip.size * 1.2; // Increased thickness
            this.shadowCtx.beginPath();
            this.shadowCtx.moveTo(lastX, lastY);
            this.shadowCtx.lineTo(drip.x, drip.y);
            this.shadowCtx.stroke();
            this.shadowCtx.globalAlpha = 1.0;
        }

        // Handle hitting the bottom
        if (drip.y >= this.height - 5) {
            const poolWidth = drip.size * (10 + Math.random() * 10);
            const poolHeight = drip.size * (1 + Math.random() * 1.5);
            this.shadowCtx.fillStyle = drip.color;
            this.shadowCtx.globalAlpha = 0.2 + Math.random() * 0.2;
            this.shadowCtx.beginPath();
            this.shadowCtx.ellipse(drip.x, this.height, poolWidth, poolHeight, 0, 0, Math.PI * 2);
            this.shadowCtx.fill();
            this.shadowCtx.globalAlpha = 1.0;
            this.drips.splice(i, 1);
        }
    }
  }

  destroy() {
    super.destroy();
    this.drips = [];
    this.sources = [];
    this.domMap = [];
    this.shadowCanvas = null;
    this.shadowCtx = null;
  }
}

/**
 * Blood Splatter Animation
 * Explosive blood splatters with particle bursts
 */
class BloodSplatterAnimation extends BaseAnimation {
  constructor() {
    super();
    this.splatters = [];
    this.particles = [];
  }

  init(container) {
    super.init(container);
    this.generateInitialSplatters();
  }

  generateInitialSplatters() {
    // Create some initial splatters
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.createSplatter(
          Math.random() * this.width,
          Math.random() * this.height
        );
      }, i * 1000);
    }
  }

  render() {
    // Dark background
    this.ctx.fillStyle = 'rgba(5, 0, 0, 0.02)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Create random splatters (much more subtle)
    if (Math.random() < 0.001) {
      this.createSplatter(
        Math.random() * this.width,
        Math.random() * this.height
      );
    }

    // Mouse interaction - create splatter near mouse (more subtle)
    if (Math.random() < 0.008) {
      const mouseRadius = 80;
      this.createSplatter(
        this.mouseX + (Math.random() - 0.5) * mouseRadius,
        this.mouseY + (Math.random() - 0.5) * mouseRadius
      );
    }

    // Update and draw particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];

      // Apply forces
      particle.vx *= 0.99; // Friction
      particle.vy += 0.1; // Gravity

      particle.x += particle.vx;
      particle.y += particle.vy;

      // Fade over time (slower for more subtle effect)
      particle.alpha -= 0.002;
      particle.size *= 0.9995;

      if (particle.alpha <= 0 || particle.size <= 0.5) {
        this.particles.splice(i, 1);
        continue;
      }

      // Draw particle
      this.ctx.fillStyle = `rgba(${particle.color.r}, ${particle.color.g}, ${particle.color.b}, ${particle.alpha})`;
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Update and draw splatters
    for (let i = this.splatters.length - 1; i >= 0; i--) {
      const splatter = this.splatters[i];

      splatter.alpha -= 0.001;

      if (splatter.alpha <= 0) {
        this.splatters.splice(i, 1);
        continue;
      }

      // Draw splatter
      this.ctx.fillStyle = `rgba(${splatter.color.r}, ${splatter.color.g}, ${splatter.color.b}, ${splatter.alpha})`;

      for (const blob of splatter.blobs) {
        this.ctx.beginPath();
        this.ctx.arc(splatter.x + blob.x, splatter.y + blob.y, blob.size, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
  }

  createSplatter(x, y) {
    const particleCount = 15 + Math.random() * 25;
    const baseColor = {
      r: 120 + Math.random() * 50,
      g: 10 + Math.random() * 20,
      b: 10 + Math.random() * 20
    };

    // Create flying particles
    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 8;

      this.particles.push({
        x: x,
        y: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1 + Math.random() * 4,
        alpha: 0.8 + Math.random() * 0.2,
        color: {
          r: baseColor.r + (Math.random() - 0.5) * 30,
          g: baseColor.g + (Math.random() - 0.5) * 10,
          b: baseColor.b + (Math.random() - 0.5) * 10
        }
      });
    }

    // Create static splatter
    const blobCount = 5 + Math.random() * 8;
    const blobs = [];

    for (let i = 0; i < blobCount; i++) {
      const distance = Math.random() * 30;
      const angle = Math.random() * Math.PI * 2;

      blobs.push({
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        size: 3 + Math.random() * 8
      });
    }

    this.splatters.push({
      x: x,
      y: y,
      blobs: blobs,
      alpha: 0.7,
      color: baseColor
    });
  }

  destroy() {
    super.destroy();
    this.splatters = [];
    this.particles = [];
  }
}

/**
 * Creeping Shadows Animation
 * Shadowy tendrils that move and respond to mouse
 */
class CreepingShadowsAnimation extends BaseAnimation {
  constructor() {
    super();
    this.shadows = [];
    this.noiseOffset = 0;
  }

  init(container) {
    super.init(container);
    this.generateShadows();
  }

  generateShadows() {
    const shadowCount = 8 + Math.floor(Math.random() * 6);

    for (let i = 0; i < shadowCount; i++) {
      this.shadows.push({
        segments: this.generateShadowPath(),
        opacity: 0.1 + Math.random() * 0.4,
        speed: 0.005 + Math.random() * 0.01,
        phase: Math.random() * Math.PI * 2,
        thickness: 20 + Math.random() * 40
      });
    }
  }

  generateShadowPath() {
    const segments = [];
    const segmentCount = 15 + Math.floor(Math.random() * 10);

    let x = Math.random() * this.width;
    let y = Math.random() * this.height;

    for (let i = 0; i < segmentCount; i++) {
      segments.push({ x: x, y: y, baseX: x, baseY: y });

      // Move to next position
      const angle = (i / segmentCount) * Math.PI * 2 + Math.random() * 0.5;
      const distance = 30 + Math.random() * 50;

      x += Math.cos(angle) * distance;
      y += Math.sin(angle) * distance;

      // Keep within bounds
      x = Math.max(0, Math.min(this.width, x));
      y = Math.max(0, Math.min(this.height, y));
    }

    return segments;
  }

  render() {
    // Very dark background with slight purple tint
    this.ctx.fillStyle = 'rgba(5, 0, 10, 0.02)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    this.noiseOffset += 0.01;

    // Update and draw shadows
    for (const shadow of this.shadows) {
      shadow.phase += shadow.speed;

      // Update segment positions with organic movement
      for (let i = 0; i < shadow.segments.length; i++) {
        const segment = shadow.segments[i];
        const noise = this.simplex2D(segment.baseX * 0.01, segment.baseY * 0.01 + this.noiseOffset);
        const timeNoise = Math.sin(shadow.phase + i * 0.5) * 0.5;

        segment.x = segment.baseX + noise * 50 + timeNoise * 20;
        segment.y = segment.baseY + Math.sin(shadow.phase + i * 0.3) * 15;

        // Mouse influence
        const mouseDist = Math.sqrt(
          Math.pow(segment.x - this.mouseX, 2) +
                    Math.pow(segment.y - this.mouseY, 2)
        );

        if (mouseDist < 150) {
          const influence = 1 - (mouseDist / 150);
          const dx = this.mouseX - segment.x;
          const dy = this.mouseY - segment.y;

          segment.x += dx * influence * 0.1;
          segment.y += dy * influence * 0.1;
        }
      }

      // Draw shadow path with dark purple/gray color
      this.ctx.strokeStyle = `rgba(30, 10, 50, ${shadow.opacity})`;
      this.ctx.lineWidth = shadow.thickness;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';

      // Create gradient along the path
      for (let i = 0; i < shadow.segments.length - 1; i++) {
        const segment = shadow.segments[i];
        const nextSegment = shadow.segments[i + 1];
        const alpha = shadow.opacity * (1 - i / shadow.segments.length);

        this.ctx.strokeStyle = `rgba(30, 10, 50, ${alpha})`;
        this.ctx.lineWidth = shadow.thickness * (1 - i / shadow.segments.length);

        this.ctx.beginPath();
        this.ctx.moveTo(segment.x, segment.y);
        this.ctx.lineTo(nextSegment.x, nextSegment.y);
        this.ctx.stroke();
      }
    }
  }

  // Simple 2D noise function
  simplex2D(x, y) {
    return Math.sin(x) * Math.cos(y) * 0.5 +
               Math.sin(x * 2.1) * Math.cos(y * 1.7) * 0.3 +
               Math.sin(x * 4.3) * Math.cos(y * 3.1) * 0.2;
  }

  destroy() {
    super.destroy();
    this.shadows = [];
  }
}

/**
 * Horror Mist Animation
 * Eerie mist particles with swirling motion
 */
class HorrorMistAnimation extends BaseAnimation {
  constructor() {
    super();
    this.mistParticles = [];
    this.swirls = [];
  }

  init(container) {
    super.init(container);
    this.generateMist();
    this.generateSwirls();
  }

  generateMist() {
    const particleCount = Math.min(200, Math.floor(this.width * this.height / 5000));

    for (let i = 0; i < particleCount; i++) {
      this.mistParticles.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: 10 + Math.random() * 30,
        opacity: 0.05 + Math.random() * 0.15,
        phase: Math.random() * Math.PI * 2,
        phaseSpeed: 0.01 + Math.random() * 0.02
      });
    }
  }

  generateSwirls() {
    const swirlCount = 3 + Math.floor(Math.random() * 3);

    for (let i = 0; i < swirlCount; i++) {
      this.swirls.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        radius: 80 + Math.random() * 120,
        strength: 0.5 + Math.random() * 1,
        rotation: 0,
        rotationSpeed: 0.005 + Math.random() * 0.01
      });
    }
  }

  render() {
    // Dark misty background
    this.ctx.fillStyle = 'rgba(5, 5, 10, 0.02)';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Update swirls
    for (const swirl of this.swirls) {
      swirl.rotation += swirl.rotationSpeed;

      // Drift slowly
      swirl.x += Math.sin(this.time * 0.001 + swirl.rotation) * 0.2;
      swirl.y += Math.cos(this.time * 0.001 + swirl.rotation) * 0.2;

      // Keep in bounds
      if (swirl.x < 0) swirl.x = this.width;
      if (swirl.x > this.width) swirl.x = 0;
      if (swirl.y < 0) swirl.y = this.height;
      if (swirl.y > this.height) swirl.y = 0;
    }

    // Update and draw mist particles
    for (const particle of this.mistParticles) {
      particle.phase += particle.phaseSpeed;

      // Base movement
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Swirl influence
      for (const swirl of this.swirls) {
        const dx = particle.x - swirl.x;
        const dy = particle.y - swirl.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < swirl.radius && distance > 0) {
          const influence = (swirl.radius - distance) / swirl.radius;
          const angle = Math.atan2(dy, dx) + swirl.rotation;
          const force = influence * swirl.strength * 0.1;

          particle.vx += Math.cos(angle) * force;
          particle.vy += Math.sin(angle) * force;
        }
      }

      // Mouse repulsion
      const mouseDx = particle.x - this.mouseX;
      const mouseDy = particle.y - this.mouseY;
      const mouseDist = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

      if (mouseDist < 100 && mouseDist > 0) {
        const repulsion = (100 - mouseDist) / 100;
        particle.vx += (mouseDx / mouseDist) * repulsion * 0.5;
        particle.vy += (mouseDy / mouseDist) * repulsion * 0.5;
      }

      // Damping
      particle.vx *= 0.99;
      particle.vy *= 0.99;

      // Wrap around edges
      if (particle.x < -particle.size) particle.x = this.width + particle.size;
      if (particle.x > this.width + particle.size) particle.x = -particle.size;
      if (particle.y < -particle.size) particle.y = this.height + particle.size;
      if (particle.y > this.height + particle.size) particle.y = -particle.size;

      // Draw particle with soft edges
      const dynamicOpacity = particle.opacity * (1 + Math.sin(particle.phase) * 0.3);
      const gradient = this.ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.size
      );

      gradient.addColorStop(0, `rgba(100, 100, 120, ${dynamicOpacity})`);
      gradient.addColorStop(0.5, `rgba(80, 80, 100, ${dynamicOpacity * 0.6})`);
      gradient.addColorStop(1, `rgba(60, 60, 80, 0)`);

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Draw swirl centers (subtle)
    for (const swirl of this.swirls) {
      const gradient = this.ctx.createRadialGradient(
        swirl.x, swirl.y, 0,
        swirl.x, swirl.y, 20
      );
      gradient.addColorStop(0, 'rgba(120, 120, 140, 0.1)');
      gradient.addColorStop(1, 'rgba(120, 120, 140, 0)');

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(swirl.x, swirl.y, 20, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  destroy() {
    super.destroy();
    this.mistParticles = [];
    this.swirls = [];
  }
}

// Export for use
window.AdvancedBackgrounds = AdvancedBackgrounds;

// Robust initialization function
function initializeAdvancedBackgrounds() {
  try {
    // Only initialize if we're on a page with advanced backgrounds
    const backgroundSelectors = [
      '.bg-neural-network',
      '.bg-particle-universe',
      '.bg-liquid-metal',
      '.bg-blood-cells',
      '.bg-cyber-rain',
      '.bg-quantum-field',
      '.bg-horror-tentacles',
      '.bg-plasma-storm',
      '.bg-dna-helix',
      '.bg-fractal-zoom',
      '.bg-blood-drip',
      '.bg-blood-splatter',
      '.bg-creeping-shadows',
      '.bg-horror-mist'
    ];

    const hasAdvancedBgs = document.querySelector(backgroundSelectors.join(', '));

    if (hasAdvancedBgs) {
      const backgrounds = new AdvancedBackgrounds();
      backgrounds.init();
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

// Multiple initialization attempts for maximum compatibility
if (document.readyState === 'loading') {
  // DOM is still loading
  document.addEventListener('DOMContentLoaded', initializeAdvancedBackgrounds);
} else if (document.readyState === 'interactive') {
  // DOM is loaded but resources might still be loading
  setTimeout(initializeAdvancedBackgrounds, 100);
} else {
  // DOM and resources are loaded
  initializeAdvancedBackgrounds();
}

// Fallback initialization on window load
window.addEventListener('load', function() {
  // Only run if not already initialized
  if (!window.advancedBackgroundsInitialized) {
    initializeAdvancedBackgrounds();
  }
});

// Mark when initialized to prevent double initialization
window.addEventListener('DOMContentLoaded', function() {
  if (initializeAdvancedBackgrounds()) {
    window.advancedBackgroundsInitialized = true;
  }
});